import { FC, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useCurrentUser } from "../../../hooks/useCurrentUser";
import { useDispatch, useSelector } from "react-redux";
import { ESRThunkDispatch } from "../../../actions/form";
import { listAllSentNotifications } from "../../../actions/util";
import { ESRState, SentNotificationT } from "../../../types/data";
import { DrawerContainer } from "../DrawerContainer";
import { Box, Divider, IconButton, Typography } from "@mui/material";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import { Close } from "@mui/icons-material";

dayjs.extend(relativeTime);

type Props = {
    open: boolean;
    onClose: () => void;
};
function colorForImportance(importance: number) {
    switch (importance) {
        case 2:
            return "#F72A2A";
        case 1:
            return "#18CE41";
        default:
            return "#C7C7C7";
    }
};

export const SentNotificationsList: FC<Props> = ({ open, onClose }) => {
    const { esrId = "", activeId = "" } = useParams();
    const [user, setUser] = useCurrentUser();
    const dispatch = useDispatch<ESRThunkDispatch>();
    const sent_notifications = useSelector((state: ESRState) => state.sent_notifications);

    useEffect(() => {
        if (esrId && open) dispatch(listAllSentNotifications(activeId));
    }, [esrId, open]);

    type GroupedSentNotif = { text: string; importance: number; timestamp: string; recipients: Array<string>; invalid: boolean };
    type SentNotifDayGroup = { date: string; notifs: Array<GroupedSentNotif> };

    const notificationsByDate = useMemo(() => {
        // date -> notification text + send-batch timestamp -> grouped notif
        // (rows sharing a text and exact timestamp were sent together, one per recipient)
        const notifsByDateByText: Record<string, Record<string, GroupedSentNotif>> = {};
        for (const notif of sent_notifications) {
            const date = dayjs(notif.timestamp).format("MM/DD/YYYY");
            const groupKey = `${notif.notification}::${notif.timestamp}`;
            if (!notifsByDateByText[date]) notifsByDateByText[date] = {};
            if (!notifsByDateByText[date][groupKey]) {
                notifsByDateByText[date][groupKey] = { text: notif.notification, importance: notif.importance, timestamp: notif.timestamp, recipients: [], invalid: notif.invalid };
            }
            const group = notifsByDateByText[date][groupKey];
            group.recipients.push(notif.recipient);
        }
        const result: Array<SentNotifDayGroup> = [];
        const dates = Object.keys(notifsByDateByText).sort((a, b) => dayjs(b).diff(a));
        for (const date of dates) {
            const notifs = Object.values(notifsByDateByText[date]);
            notifs.sort((a, b) => dayjs(b.timestamp).diff(a.timestamp));
            result.push({ date, notifs });
        }
        return result;
    }, [sent_notifications]);

    return (
        <DrawerContainer open={open} onClose={onClose}>
            <Box
                sx={{
                    width: 500,
                    height: "100%",
                    background: "rgba(0, 62, 98, 0.02)",
                    display: "flex",
                    flexDirection: "column",
                    p: 2,
                }}
            >
                <Box
                    sx={{
                        height: 60,
                        display: "flex",
                        flexDirection: "row",
                        alignItems: "center",
                        px: "24px",
                        justifyContent: "space-between",
                    }}
                >
                    <Typography sx={{ fontSize: 24, fontWeight: 700 }}>
                        Sent Notifications
                    </Typography>

                    <IconButton aria-label="close files" onClick={onClose}>
                        <Close />
                    </IconButton>
                </Box>

                <Box
                    sx={{
                        flex: 1,
                        height: "100%",
                        boxSizing: "border-box",
                        overflowY: "auto",
                        p: 2
                    }}
                >
                    {notificationsByDate.map(({ date, notifs }, index) => (
                        <Box key={index} sx={{ mb: 2 }}>
                            <Typography
                                sx={{
                                    fontSize: 14,
                                    fontWeight: 600,
                                    color: "#3f51b5",
                                    mb: 1,
                                }}
                            >
                                {date}
                            </Typography>
                            <Box>
                                {notifs.map((notif, index) => (
                                    <Box
                                        key={index}
                                        sx={{                                            
                                            background: "#fff",
                                            borderRadius: 2,
                                            p: 2,
                                            boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.1)",
                                            mb: 1,
                                            opacity: notif.invalid ? 0.5 : 1,
                                        }}
                                    >
                                            <Typography
                                                fontWeight="bold"
                                                fontSize={16}
                                                // color={colorForImportance(notif.importance)}
                                                sx={{ mb: 1,
                                                    whiteSpace: "pre-line",                                   
                                                    ...(notif.invalid && {
                                                        textDecoration: "line-through",
                                                    }),
                                                }}
                                            >
                                                {notif.text}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    color: "#555",
                                                    fontSize: 14,
                                                }}
                                            >
                                                Sent To: {notif.recipients.map((recipient, i) => (
                                                    <span key={i}>{i > 0 && ", "}{recipient}</span>
                                                ))}
                                            </Typography>
                                    </Box>
                                ))}
                            </Box>
                        </Box>
                    ))}
                    {
                        sent_notifications.length === 0 && 
                        <Box
                            sx={{
                                height: "100%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "text.secondary",
                                fontStyle: "italic",
                            }}
                        >
                            No notifications to view.
                        </Box>
                    }
                </Box>
            </Box>
        </DrawerContainer>

    )
}